import { ReactElement, useState, useEffect } from 'react';

import { SolutionSessionMessage } from '@backrs_ui/grow-ui/src/models/models';
import { useGetEmployersByNameQuery } from '@backrs_ui/grow-ui/src/redux/employer/employer.api';
import {
  useCreateEmploymentHistoryMutation,
  useUpdateEmploymentHistoryMutation,
  useGetUserEmploymentHistoryQuery,
  UserEmploymentHistoryRequest,
} from '@backrs_ui/grow-ui/src/redux/userPortfolio/userEmploymentHistory.api';

import { WORK_EXPERIENCE_FIELDS } from './questions.config';
import { ResumeQuestionForm } from './ResumeQuestionForm';
import { FormValue, FormValues } from './types';
import {
  prePopulateFormWithEmploymentHistory,
  buildEmploymentHistoryFromFormValues,
} from './work.utils';

type WorkExperienceQuestionProps = Readonly<{
  sendQAMessage: (message: string, LLMInput?: string) => void;
  questionNumber?: number;
  message?: SolutionSessionMessage;
}>;

export const WorkExperienceQuestion = ({
  sendQAMessage,
  questionNumber = 1,
  message,
}: WorkExperienceQuestionProps): ReactElement => {
  const [createEmploymentHistory] = useCreateEmploymentHistoryMutation();
  const [updateEmploymentHistory] = useUpdateEmploymentHistoryMutation();
  const [employmentHistoryId, setEmploymentHistoryId] = useState<number | null>(null);
  const [selectedEmployerName, setSelectedEmployerName] = useState<string>('');
  const [initialValues, setInitialValues] = useState<FormValues>({});

  const { data: existingEmploymentHistory, refetch: refetchEmploymentHistory } =
    useGetUserEmploymentHistoryQuery(undefined);

  const { data: employerData } = useGetEmployersByNameQuery([selectedEmployerName], {
    skip: !selectedEmployerName,
  });

  useEffect(() => {
    if (message) {
      refetchEmploymentHistory();
    }
  }, [message, refetchEmploymentHistory]);

  useEffect(() => {
    if (existingEmploymentHistory && existingEmploymentHistory.length > 0) {
      const result = prePopulateFormWithEmploymentHistory(
        existingEmploymentHistory,
        questionNumber,
      );

      if (result) {
        setEmploymentHistoryId(result.employmentHistoryId);
        setInitialValues(result.formValues);
        setSelectedEmployerName(result.selectedEmployerName);
      }
    }
  }, [existingEmploymentHistory, questionNumber]);

  const handleFieldChange = (fieldKey: string, values: FormValue[]) => {
    if (fieldKey === 'careerEmployer' && values.length > 0) {
      const employerName = typeof values[0] === 'string' ? values[0] : String(values[0]);

      setSelectedEmployerName(employerName);
    }
  };

  const handleSubmit = async (formValues: FormValues) => {
    const employerId = employerData && employerData.length > 0 ? employerData[0].id : undefined;
    const employmentHistoryData = buildEmploymentHistoryFromFormValues(formValues, employerId);

    if (Object.keys(employmentHistoryData).length === 0) return;

    try {
      if (employmentHistoryId) {
        await updateEmploymentHistory({
          id: employmentHistoryId,
          data: employmentHistoryData as UserEmploymentHistoryRequest,
        }).unwrap();
      } else {
        await createEmploymentHistory(
          employmentHistoryData as UserEmploymentHistoryRequest,
        ).unwrap();
      }
    } catch (error) {
      console.error('Error updating employment history:', error);
    }
  };

  const getDefaultValue = (
    fieldKey: string,
    formValues: FormValues,
  ): FormValue | FormValue[] | undefined => {
    if (!formValues[fieldKey] || formValues[fieldKey].length === 0) return undefined;

    switch (fieldKey) {
      case 'careerEmployer':
        if (formValues[fieldKey].length > 0) {
          return formValues[fieldKey][0];
        }

        return undefined;
      default:
        return formValues[fieldKey];
    }
  };

  return (
    <ResumeQuestionForm
      fields={WORK_EXPERIENCE_FIELDS}
      sendQAMessage={sendQAMessage}
      onFieldChange={handleFieldChange}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      getDefaultValue={getDefaultValue}
    />
  );
};
